'use client';

import { type ThreadMessage, useAuiState } from '@assistant-ui/react';

import { isApprovalPending } from '@/components/assistant-ui/approval/approval-state';
import { classifyToolPart } from '@/components/assistant-ui/thread/activity-trail-helpers';
import type { AnyToolCallPart } from '@/components/assistant-ui/thread/trail-records';
import type { ToolCallApproval } from '@/components/assistant-ui/tool-status';
import { useReadOnly } from '@/components/providers/read-only-provider';

import { useHitl } from './hitl-provider';

interface HitlPartShape {
  type?: string;
  toolName?: string;
  toolCallId?: string;
  result?: { status?: string } | unknown;
  approval?: ToolCallApproval;
}

export type HitlCallLane = 'human' | 'approval';

// Include the HITL identity and outcome in the activity trail's memoization key.
export function findHitlCallKeyInParts({
  parts,
  humanToolNames,
}: {
  parts: readonly unknown[];
  humanToolNames: ReadonlySet<string>;
}): string {
  for (const part of parts as ReadonlyArray<HitlPartShape>) {
    if (part.type !== 'tool-call' || !part.toolCallId) continue;
    if (part.approval) {
      const resolved = !isApprovalPending(part.approval);
      return `${part.toolCallId}|${resolved ? 'responded' : ''}|approval`;
    }
    if (part.toolName && humanToolNames.has(part.toolName)) {
      const status = (part.result as { status?: string } | undefined)?.status ?? '';
      return `${part.toolCallId}|${status}|human`;
    }
  }
  return '';
}

/**
 * `findHitlCallKeyInParts` for the current message. A joined string keeps `useAuiState`
 * referentially stable across re-renders.
 */
export function useHitlCallKey(): string {
  const { humanToolNames } = useHitl();
  return useAuiState(({ message }) =>
    findHitlCallKeyInParts({ parts: message?.parts ?? [], humanToolNames }),
  );
}

export function parseHitlCallKey(key: string): {
  toolCallId: string;
  resultStatus: string;
  lane: HitlCallLane | '';
} {
  const [toolCallId = '', resultStatus = '', lane = ''] = key.split('|');
  return { toolCallId, resultStatus, lane: lane as HitlCallLane | '' };
}

export function isAwaitingHitlAction(status: ThreadMessage['status']): boolean {
  return status?.type === 'requires-action' && status.reason !== 'interrupt';
}

function hasPendingApproval(parts: readonly unknown[]): boolean {
  return (parts as readonly AnyToolCallPart[]).some(
    part => part.type === 'tool-call' && classifyToolPart(part) === 'awaiting-approval',
  );
}

function hasResultlessGuestCall({
  parts,
  humanToolNames,
}: {
  parts: readonly unknown[];
  humanToolNames: ReadonlySet<string>;
}): boolean {
  return (parts as ReadonlyArray<HitlPartShape>).some(
    part =>
      part.type === 'tool-call' &&
      part.result === undefined &&
      (!!part.approval || (!!part.toolName && humanToolNames.has(part.toolName))),
  );
}

/**
 * assistant-ui reports any result-less call as `tool-calls`, so a run the guest stopped mid-tool
 * looks like a card waiting on them; only a human tool or a gate (answered but not yet resumed
 * counts) waits. An unanswered gate parks the run as an `interrupt` instead, read from its part.
 */
export function waitsOnGuest({
  status,
  parts,
  readOnly,
  humanToolNames,
}: {
  status: ThreadMessage['status'];
  parts: readonly unknown[];
  readOnly: boolean;
  humanToolNames: ReadonlySet<string>;
}): boolean {
  if (isAwaitingHitlAction(status) && hasResultlessGuestCall({ parts, humanToolNames })) {
    return true;
  }
  return !readOnly && hasPendingApproval(parts);
}

export function useWaitingOnGuest(): boolean {
  const readOnly = useReadOnly();
  const { humanToolNames } = useHitl();
  return useAuiState(({ message }) =>
    waitsOnGuest({
      status: message?.status,
      parts: message?.parts ?? [],
      readOnly,
      humanToolNames,
    }),
  );
}

/** The model writes its answer after the gate, so result cards shown before the run resumes would
 *  sit beside no text. */
export function useResultsHeldForGuest(): boolean {
  const readOnly = useReadOnly();
  const waitingOnGuest = useWaitingOnGuest();
  const isLast = useAuiState(({ message }) => message?.isLast !== false);
  return !readOnly && isLast && waitingOnGuest;
}
